import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { User } from "@/common/types/user";
import { useTRPC } from "@/trpc";
import {
  getAllOrgOnboardingSteps,
  getOrgOnboardingSteps,
} from "../utils/getOrgOnboardingSteps";
import { OrgOnboardingStep } from "../enums/OrgOnboardingStep";
import { OrganizationTermsCard } from "@/components/settings/organization-terms-card";
import { useOrganization } from "@/hooks/use-organization";

export default function OrgOnboardingContainer({ user }: { user: User }) {
  const [step, setStep] = useState<number>(0);
  const [initialized, setInitialized] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const { selectedOrg } = useOrganization();
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const allSteps = useMemo(() => getAllOrgOnboardingSteps(), []);

  // Initialize step to first incomplete step on mount
  useEffect(() => {
    if (!user || initialized) {
      return;
    }

    const incompleteSteps = getOrgOnboardingSteps(user);

    if (incompleteSteps.length === 0) {
      setShowTerms(true);
      setInitialized(true);
      return;
    }

    // Find the index of the first incomplete step
    const firstIncompleteStep = allSteps.findIndex(
      (s) => s.id === incompleteSteps[0].id,
    );

    if (firstIncompleteStep !== -1) {
      setStep(firstIncompleteStep);
    }
    setInitialized(true);
  }, [user?.id, allSteps, initialized, navigate]);

  const onComplete = async () => {
    if (step == null) {
      return;
    }

    // Refresh user data to get updated completion status
    await queryClient.invalidateQueries({
      queryKey: trpc.user.getUser.queryKey(),
    });

    if (step === allSteps.length - 1) {
      // Organization terms are a separate confirmation after personal details.
      setShowTerms(true);
      return;
    }

    setStep((currentStep) => currentStep + 1);
  };

  const onBack = () => {
    if (step > 0) {
      setStep((currentStep) => currentStep - 1);
    }
  };

  const _onNavigateToStep = (stepId: OrgOnboardingStep) => {
    const targetIndex = allSteps.findIndex((s) => s.id === stepId);
    if (targetIndex !== -1) {
      setStep(targetIndex);
    }
  };

  const stepData = allSteps[step];
  const Component = step == null ? step : stepData?.component;

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "auto", block: "start" });
    }

    window.scrollTo({ top: 0, behavior: "auto" });
  }, [user, step, showTerms]);

  if (!user || Component == null) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="bg-gray-100 lg:p-2 lg:pt-4 md:pt-8 md:p-8 min-h-screen"
    >
      <div className="max-w-lg mx-auto">
        {showTerms ? (
          <OrganizationTermsCard
            organizationId={selectedOrg.id}
            user={user}
            onContinue={() =>
              void navigate({ to: "/org/home", resetScroll: false })
            }
          />
        ) : (
          <div className="animate-fade-in-delay-2 w-full p-4 pt-8 pb-12 md:p-8 md:pt-8 lg:rounded-lg lg:border bg-white">
            <Component
              onComplete={onComplete}
              onBack={step > 0 ? onBack : undefined}
            />
          </div>
        )}
      </div>
    </div>
  );
}
